/**
 * Homepage API Service
 * Handles all API calls for the Homepage Builder feature
 *
 * The API uses typed validation for each section type.
 * Each section has specific required and optional fields.
 */

import { apiClient } from "./apiClient";
import { SectionData, SectionType } from "@/components/shared/homepage/types";
import logger from "@/utils/logger";

// ============ RESPONSE TYPES ============

interface ApiResponse<T = any> {
  success: number;
  message: string;
  data?: T;
  error?: string;
  errors?: Array<{
    field?: string;
    section?: number;
    type?: string;
    message: string;
  }>;
}

interface SaveHomepageResponse extends ApiResponse {
  total_sections: number;
}

// Subdomain info returned with homepage load
export interface SubdomainInfo {
  uuid?: string;
  subdomain: string;
  name: string;
  logo: string | null; // URL to the logo image (e.g., '/uploads/logos/filename.jpg')
  favicon?: string;
  template?: string; // e.g. 'beryllium', 'dark', 'light', 'professional'
  primaryColor?: string;
  role?: string; // e.g., 'institutions', 'vendors'
  userId?: string | number;
  createdAt?: string;
}

export interface OwnerDetails {
  uuid?: string;
  name?: string;
  email?: string;
  logo?: string;
  id?: string | number;
  branding_color?: string; // Color from vendor/institution table
  primary_color?: string;
}

interface LoadHomepageResponse extends ApiResponse<SectionData[]> {
  globalStyle?: GlobalStyle;
  subdomainInfo?: SubdomainInfo;
  ownerDetails?: OwnerDetails;
}

interface SectionTypesResponse
  extends ApiResponse<Record<SectionType, SectionTypeInfo>> {
  types: SectionType[];
}

interface UploadImageResponse
  extends ApiResponse<{
    filename: string;
    url: string;
    logo?: string;
  }> {
  url?: string;
}

// Response for getSectionById
export interface SectionByIdData {
  id: string;           // Frontend ID (e.g., "footer-ai")
  type: SectionType;    // Section type (e.g., "footer", "hero")
  title: string;        // Display title
  content: Record<string, any>;  // Section content object
  isVisible: boolean;   // Whether section is published
  _dbId: number;        // Database primary key
  position: number;     // Sort order
  updated_at: string;   // Last update timestamp
  date: string | null;  // Lock expiry date (null = not locked)
}

interface GetSectionByIdResponse extends ApiResponse<SectionByIdData> { }

// Response for updateSectionContent
interface UpdateSectionContentResponse extends ApiResponse<{
  id: number;
  section_type: string;
}> { }

// ============ CONTENT TYPES FOR EACH SECTION ============

export interface GlobalStyle {
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  borderRadius?: string;
  containerWidth?: string;
}

export interface ColorPalette {
  primary?: string;
  secondary?: string;
  accent?: string;
  background?: string;
  text?: string;
}

// Header Section
export interface HeaderContent {
  logo: string; // Required
  navItems: string[]; // Required - at least one item
  ctaText?: string;
  ctaLink?: string;
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Hero Section
export interface HeroContent {
  title: string; // Required
  subtitle: string; // Required
  highlightText?: string;
  primaryCta?: string;
  primaryCtaLink?: string;
  secondaryCta?: string;
  secondaryCtaLink?: string;
  imageUrl?: string;
  image?: string;
  align?: "left" | "center" | "right";
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Info Section
export interface InfoCard {
  icon?: string;
  title: string;
  text: string;
}

export interface InfoContent {
  title: string; // Required
  layout?: "left-image" | "right-image" | "full-text" | "full-image" | "split";
  subtitle?: string;
  description?: string;
  image?: string;
  cards?: InfoCard[];
  splitContent?: {
    left?: { title: string; text: string; icon?: string };
    right?: { title: string; text: string; icon?: string };
  };
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Features Section
export interface FeatureItem {
  icon?: string;
  title: string; // Required
  description: string; // Required
  link?: string;
}

export interface FeaturesContent {
  title: string; // Required
  features: FeatureItem[]; // Required - at least one
  subtitle?: string;
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Stats Section
export interface StatItem {
  value: string; // Required
  label: string; // Required
  icon?: string;
  prefix?: string;
  suffix?: string;
}

export interface StatsContent {
  title: string; // Required
  stats: StatItem[]; // Required - at least one
  subtitle?: string;
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Testimonials Section
export interface TestimonialItem {
  name: string; // Required
  text: string; // Required
  role?: string;
  company?: string;
  avatar?: string;
  rating?: number; // 1-5
}

export interface TestimonialsContent {
  title: string; // Required
  testimonials: TestimonialItem[]; // Required - at least one
  subtitle?: string;
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// CTA Section
export interface CtaContent {
  title: string; // Required
  buttonText: string; // Required
  subtitle?: string;
  buttonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Companies Section
export interface CompanyItem {
  name: string;
  logo?: string;
  link?: string;
}

export interface CompaniesContent {
  title: string; // Required
  companies: (string | CompanyItem)[]; // Required - at least one
  subtitle?: string;
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Footer Section
export interface FooterContent {
  companyName: string; // Required
  logo?: string;
  links?: (string | { text: string; url?: string })[];
  copyright?: string;
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    youtube?: string;
  };
  colorPalette?: ColorPalette;
  fontFamily?: string;
}

// About Section
export interface AboutContent {
  title: string; // Required
  description: string; // Required
  imageUrl?: string;
  mission?: string;
  vision?: string;
  values?: string[];
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Services Section
export interface ServiceItem {
  title: string; // Required
  description: string; // Required
  icon?: string;
  link?: string;
  price?: string;
}

export interface ServicesContent {
  title: string; // Required
  services: ServiceItem[]; // Required - at least one
  subtitle?: string;
  colorPalette?: ColorPalette;
  fontFamily?: string;
  variant?: "v1" | "v2" | "v3";
}

// Union type for all content types
export type SectionContent =
  | HeaderContent
  | HeroContent
  | InfoContent
  | FeaturesContent
  | StatsContent
  | TestimonialsContent
  | CtaContent
  | CompaniesContent
  | FooterContent
  | AboutContent
  | ServicesContent;

// Section type info (from API)
export interface SectionTypeInfo {
  label: string;
  requiredFields: string[];
  optionalFields: string[];
}

// ============ API SERVICE ============

export const homepageApi = {
  /**
   * Get list of valid section types with their required/optional fields
   * Useful for building dynamic forms
   */
  getSectionTypes: async (): Promise<SectionTypesResponse> => {
    return apiClient.get("/homepage/section-types");
  },

  /**
   * Save all homepage sections (bulk save) as DRAFTS
   * This replaces all sections for the current user
   *
   * Each section's content is validated based on its type.
   * See the content interfaces above for required fields.
   */
  save: async (
    sections: SectionData[],
    globalStyle?: GlobalStyle,
  ): Promise<SaveHomepageResponse> => {
    const payload = {
      sections: sections.map((section, index) => ({
        id: section.id,
        type: section.type,
        title: section.title,
        content: section.content,
        isVisible: section.isVisible, // Respect current visibility state
        position: index,
        date: section.date,
        _dbId: section._dbId,
      })),
      globalStyle,
    };

    return apiClient.post("/homepage/save", payload);
  },

  /**
   * Load all homepage sections for the current authenticated user
   */
  load: async (): Promise<LoadHomepageResponse> => {
    const response = await apiClient.get("/homepage/load");

    if (response.success && Array.isArray(response.data)) {
      const dynamicLogo = response.subdomainInfo?.logo || response.ownerDetails?.logo;
      const companyName = response.subdomainInfo?.name || response.ownerDetails?.name || 'Company';

      response.data = response.data.map((item: any) => {
        let parsedContent = item.content;
        if (typeof item.content === "string") {
          try {
            parsedContent = JSON.parse(item.content);
          } catch (e) {
            logger.error("Failed to parse section content", e);
            parsedContent = {};
          }
        }

        const sectionType = item.section || item.type || "unknown";

        // Inject dynamic logo/name
        if (sectionType === 'header' && dynamicLogo && !parsedContent.logo) {
          parsedContent = { ...parsedContent, logo: dynamicLogo };
        }
        if (sectionType === 'footer') {
          if (dynamicLogo && !parsedContent.logo) {
            parsedContent = { ...parsedContent, logo: dynamicLogo };
          }
          if (companyName && !parsedContent.companyName) {
            parsedContent = { ...parsedContent, companyName: companyName };
          }
        }

        return {
          id: item.id?.toString() || crypto.randomUUID(),
          type: sectionType,
          title: item.title || item.section || "Untitled",
          content: parsedContent || {},
          isVisible: item.publish !== 0, // Map publish (1/0) to boolean, default true if missing/1
          position: item.position ?? 0,
          date: item.date || null,
          _dbId: item.id
        };
      });

      response.data.sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
    }

    return response;
  },

  /**
   * Load homepage sections for a specific user (public view)
   * @param userId - The user ID to load sections for
   */
  loadByUserId: async (
    userId: string | number,
  ): Promise<LoadHomepageResponse> => {
    return apiClient.get(`/homepage/load/${userId}`);
  },

  loadBySubdomain: async (
    subdomain?: string,
  ): Promise<LoadHomepageResponse> => {
    if (!subdomain) {
      logger.error("Subdomain is required for loadBySubdomain");
      return { success: 0, message: "Subdomain is required", errors: [] };
    }

    const endpoint = `/subdomain/homepage/${encodeURIComponent(subdomain)}`;

    let response;
    try {
      response = await apiClient.get(endpoint);
      logger.debug("[homepageApi.loadBySubdomain] Raw API response:", JSON.stringify(response, null, 2));
    } catch (e) {
      logger.error("Fetch failed", e);
      return { success: 0, message: "Failed to fetch", errors: [] };
    }

    // Map backend response fields to frontend SectionData structure
    // Backend returns: { id, section, content (string), publish (1/0), ... }
    // Frontend expects: { id (string), type, content (object), isVisible (bool), ... }
    if (response.success && Array.isArray(response.data)) {
      // Extract dynamic logo from subdomainInfo or ownerDetails
      // subdomainInfo.logo comes as '/uploads/logos/filename.jpg' from backend
      const dynamicLogo = response.subdomainInfo?.logo || response.ownerDetails?.logo;
      const companyName = response.subdomainInfo?.name || response.ownerDetails?.name || 'Company';

      // First filter to only include published sections (publish = 1)
      // TEMPORARILY DISABLED for testing - show all sections including drafts
      const publishedSections = response.data.filter(
        (item: any) => item.publish === 1
      );
      // If no published sections found, fall back to ALL sections (for testing)
      const finalSections = publishedSections.length > 0 ? publishedSections : response.data;
      logger.debug("[homepageApi.loadBySubdomain] Total sections:", response.data.length, "Published sections:", publishedSections.length, "Fallback used:", publishedSections.length === 0);

      response.data = finalSections.map((item: any) => {
        let parsedContent = item.content;
        // Try parsing if it's a string
        if (typeof item.content === "string") {
          try {
            parsedContent = JSON.parse(item.content);
          } catch (e) {
            logger.error("Failed to parse section content", e);
            parsedContent = {}; // Fallback
          }
        }

        const sectionType = item.section || item.type || "unknown";

        // Inject dynamic logo into header and footer sections if not already set
        if (sectionType === 'header' && dynamicLogo && !parsedContent.logo) {
          parsedContent = { ...parsedContent, logo: dynamicLogo };
        }
        if (sectionType === 'footer') {
          if (dynamicLogo && !parsedContent.logo) {
            parsedContent = { ...parsedContent, logo: dynamicLogo };
          }
          if (companyName && !parsedContent.companyName) {
            parsedContent = { ...parsedContent, companyName: companyName };
          }
        }

        return {
          id: item.id?.toString() || crypto.randomUUID(),
          type: sectionType,
          title: item.title || item.section || "Untitled",
          content: parsedContent || {},
          isVisible: true, // All sections (published + draft fallback)
          position: item.position ?? 0,
          date: item.date || null,
        };
      });

      // Sort by position if available which ensures correct order
      response.data.sort(
        (a: any, b: any) => (a.position || 0) - (b.position || 0),
      );
    }

    return response;
  },

  /**
   * Update a single section by database ID
   * Content is validated based on the section type
   */
  updateSection: async (
    id: number,
    updates: {
      type?: SectionType;
      title?: string;
      content?: SectionContent;
      position?: number;
      isVisible?: boolean;
      date?: string | null;
    },
  ): Promise<ApiResponse> => {
    return apiClient.post("/homepage/update", { id, ...updates });
  },

  /**
   * Delete a single section by database ID
   */
  deleteSection: async (id: number): Promise<ApiResponse> => {
    return apiClient.post("/homepage/delete", { id });
  },

  /**
   * Get a single section by database ID
   * @deprecated Use getSectionById for full section data with proper typing
   */
  getSection: async (id: number): Promise<ApiResponse<SectionData>> => {
    return apiClient.get(`/homepage/section/${id}`);
  },

  /**
   * Get a single section by its database ID with full details
   * Used for content-only editing when layout is locked
   * @param sectionId - The database ID (_dbId) of the section
   * @returns Section data including content, type, position, date, etc.
   */
  getSectionById: async (sectionId: number): Promise<GetSectionByIdResponse> => {
    const response = await apiClient.get(`/homepage/section/${sectionId}`);

    // Parse content if it's a string
    if (response.success && response.data) {
      if (typeof response.data.content === "string") {
        try {
          response.data.content = JSON.parse(response.data.content);
        } catch (e) {
          logger.error("Failed to parse section content", e);
          response.data.content = {};
        }
      }
    }

    return response;
  },

  /**
   * Update only the CONTENT of a section (text, images, etc.)
   * Server protects style fields (colorPalette, fontFamily, variant, etc.)
   * Used for content-only editing when layout is locked
   * 
   * @param sectionId - The database ID (_dbId) of the section
   * @param content - The new content object (only editable fields will be saved)
   * @returns Success response with section ID and type
   * 
   * @example
   * // Update hero section text
   * await homepageApi.updateSectionContent(5, {
   *   title: "New Title",
   *   subtitle: "New Subtitle",
   *   primaryCta: "Get Started"
   * });
   */
  updateSectionContent: async (
    sectionId: number,
    content: Record<string, any>
  ): Promise<UpdateSectionContentResponse> => {
    return apiClient.post("/homepage/update-content", {
      id: sectionId,
      content: content,
    });
  },

  /**
   * Publish all homepage sections
   * This will set publish = 1 for all sections belonging to the current user
   * @returns Success response with total_sections count
   */
  publishHomepage: async (): Promise<
    ApiResponse<{ total_sections: number }>
  > => {
    return apiClient.post("/homepage/toggle-visibility", {});
  },

  /**
   * @deprecated Use publishHomepage instead - backend now publishes all sections at once
   * Toggle section visibility (publish/unpublish)
   */
  toggleVisibility: async (
    id?: number,
  ): Promise<ApiResponse<{ isVisible?: boolean; total_sections?: number }>> => {
    return apiClient.post("/homepage/toggle-visibility", id ? { id } : {});
  },

  /**
   * Upload an image for homepage sections
   * @param file - The image file to upload
   * @returns URL of the uploaded image
   */
  uploadImage: async (file: File): Promise<UploadImageResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post("/homepage/upload-image", formData);
  },

  /**
   * Upload organization/subdomain logo
   * This updates the logo in the subdomains table and the respective entity table (institutions/vendors)
   * @param file - The logo image file to upload
   * @returns URL of the uploaded logo
   */
  uploadLogo: async (file: File): Promise<UploadImageResponse> => {
    const formData = new FormData();
    formData.append("logo", file);
    const response: any = await apiClient.post("/subdomain/upload-logo", formData);

    // Normalize response if flat (some endpoints return {success, url} instead of {success, data: {url}})
    if (response.url && !response.data) {
      return {
        ...response,
        data: {
          url: response.url,
          filename: response.name || file.name,
        },
      };
    }
    return response;
  },

  submitQuery: async (data: {
    name: string;
    email: string;
    phone?: string;
    subject?: string;
    message: string;
  }): Promise<ApiResponse> => {
    return apiClient.post("/homepage/submit-query", data);
  },

  markNotificationAsRead: async (notificationId: string): Promise<ApiResponse> => {
    return apiClient.post(`/homepage/notifications/${notificationId}/read`);
  },

  markAllNotificationsAsRead: async (): Promise<ApiResponse> => {
    return apiClient.post("/homepage/notifications/mark-all-read");
  },

  getNotifications: async (limit: number = 20, skip: number = 0, status?: string): Promise<ApiResponse> => {
    const statusParam = status ? `&status=${status}` : '';
    return apiClient.get(`/homepage/notifications?limit=${limit}&skip=${skip}${statusParam}`);
  },

  getUnreadCount: async (): Promise<{ success: number; count: number }> => {
    return apiClient.get("/homepage/notifications/unread-count");
  },
};

export default homepageApi;
